import { useState, useMemo } from 'react'
import type {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  Row,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ChevronDown,
  ChevronRight,
  Map as MapIcon,
  MoreHorizontal,
  Pencil,
  Plane,
  Download,
} from 'lucide-react'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTablePagination } from '@/components/data-table/data-table-pagination'
import type {
  SpaceManagementUnit,
  SpaceManagementArea,
  ColumnGroupKey,
} from '@/types/space-management'
import { COLUMN_GROUPS } from '@/types/space-management'
import { UNIT_TYPE_LABELS, UNIT_STATUS_LABELS } from '@/types/units'
import type { UnitType, UnitStatus } from '@/types/units'
import { AREA_TYPE_LABELS } from '@/types/hangars'

// ============================================
// PROPS
// ============================================

interface SpaceManagementTableProps {
  areas: SpaceManagementArea[]
  isLoading: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  onViewOnMap: (unitId: string, areaId: string) => void
  onEditUnit: (unitId: string) => void
  onEditArea: (areaId: string) => void
}

// ============================================
// BADGE VARIANTS
// ============================================

const statusVariants: Record<string, 'default' | 'secondary' | 'outline'> = {
  available: 'default',
  leased: 'secondary',
  maintenance: 'outline',
}

const typeVariants: Record<string, 'default' | 'secondary' | 'outline'> = {
  conventional_hangar: 'default',
  box_hangar: 'default',
  t_hangar: 'default',
  tiedown: 'outline',
  ramp: 'outline',
  office: 'secondary',
  custom: 'secondary',
}

// ============================================
// COLUMN GROUP TOGGLE
// ============================================

function ColumnGroupToggle({
  activeGroups,
  onToggle,
}: {
  activeGroups: Set<ColumnGroupKey>
  onToggle: (key: ColumnGroupKey) => void
}) {
  return (
    <div className="flex gap-1">
      {COLUMN_GROUPS.map((group) => (
        <Button
          key={group.key}
          variant={activeGroups.has(group.key) ? 'default' : 'outline'}
          size="sm"
          className="h-8 text-xs"
          onClick={() => onToggle(group.key)}
          disabled={group.key === 'basic'}
          title={group.description}
        >
          {group.label}
        </Button>
      ))}
    </div>
  )
}

// ============================================
// CELL RENDERERS
// ============================================

function renderCount(value: number) {
  if (value === 0) return <span className="text-muted-foreground">--</span>
  return (
    <span className="flex items-center gap-1 font-mono text-sm">
      <Plane className="h-3 w-3" />
      {value}
    </span>
  )
}

function formatCurrency(value: number | null): string {
  if (value == null) return '--'
  return `$${value.toLocaleString()}`
}

function formatNumber(value: number | null): string {
  if (value == null) return '--'
  return value.toLocaleString()
}

// ============================================
// TABLE COMPONENT
// ============================================

export function SpaceManagementTable({
  areas,
  isLoading,
  searchQuery,
  onSearchChange,
  onViewOnMap,
  onEditUnit,
  onEditArea,
}: SpaceManagementTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [expandedAreas, setExpandedAreas] = useState<Record<string, boolean>>(
    {}
  )
  const [activeColumnGroups, setActiveColumnGroups] = useState<
    Set<ColumnGroupKey>
  >(
    new Set(
      COLUMN_GROUPS.filter((g) => g.defaultVisible).map((g) => g.key)
    )
  )

  // Flatten all units for TanStack Table
  const flatUnits = useMemo(() => {
    return areas.flatMap((area) => area.units)
  }, [areas])

  // Compute columnVisibility from activeColumnGroups
  const columnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {}
    for (const group of COLUMN_GROUPS) {
      const isVisible = activeColumnGroups.has(group.key)
      for (const colId of group.columns) {
        visibility[colId] = isVisible
      }
    }
    return visibility
  }, [activeColumnGroups])

  // Column definitions
  const columns: ColumnDef<SpaceManagementUnit>[] = useMemo(
    () => [
      // === BASIC GROUP ===
      {
        accessorKey: 'unit_code',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Unit Code" />
        ),
        cell: ({ row }) => (
          <span className="font-mono font-medium">
            {row.getValue('unit_code')}
          </span>
        ),
        enableHiding: false,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Name" />
        ),
        cell: ({ row }) => {
          const name = row.getValue('name') as string | null
          return name ? (
            <span className="text-sm">{name}</span>
          ) : (
            <span className="text-muted-foreground">--</span>
          )
        },
      },
      {
        accessorKey: 'type',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Category" />
        ),
        cell: ({ row }) => {
          const type = row.getValue('type') as UnitType | null
          if (!type) return <span className="text-muted-foreground">--</span>
          return (
            <Badge variant={typeVariants[type] || 'default'}>
              {UNIT_TYPE_LABELS[type] || type}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'usable_sqft',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Size (sqft)" />
        ),
        cell: ({ row }) => {
          const sqft = row.getValue('usable_sqft') as number | null
          return (
            <span className="font-mono text-sm">{formatNumber(sqft)}</span>
          )
        },
      },

      // === STATUS GROUP ===
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => {
          const status = row.getValue('status') as UnitStatus
          return (
            <Badge variant={statusVariants[status] || 'default'}>
              {UNIT_STATUS_LABELS[status] || status}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'placed_aircraft_count',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="# Placed" />
        ),
        cell: ({ row }) =>
          renderCount(row.getValue('placed_aircraft_count') as number),
      },
      {
        accessorKey: 'assigned_aircraft_count',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="# Assigned" />
        ),
        cell: ({ row }) =>
          renderCount(row.getValue('assigned_aircraft_count') as number),
      },
      {
        accessorKey: 'lease_client_name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Tenant" />
        ),
        cell: ({ row }) => {
          const name = row.getValue('lease_client_name') as string | null
          return name ? (
            <span className="text-sm">{name}</span>
          ) : (
            <span className="text-muted-foreground">--</span>
          )
        },
      },

      // === CONSTRAINTS GROUP ===
      {
        accessorKey: 'door_height',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Door H (ft)" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {formatNumber(row.getValue('door_height'))}
          </span>
        ),
      },
      {
        accessorKey: 'door_width',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Door W (ft)" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {formatNumber(row.getValue('door_width'))}
          </span>
        ),
      },
      {
        accessorKey: 'max_wingspan',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Max Wingspan (ft)" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {formatNumber(row.getValue('max_wingspan'))}
          </span>
        ),
      },

      // === PERFORMANCE GROUP ===
      {
        id: 'expected_monthly_rate',
        header: 'Expected Rate',
        cell: ({ row }) => {
          const unit = row.original
          const rate =
            unit.lease_monthly_rate ??
            unit.unit_monthly_rate ??
            unit.default_monthly_rate
          return (
            <span className="font-mono text-sm">{formatCurrency(rate)}</span>
          )
        },
      },
      {
        id: 'occupancy_placeholder',
        header: 'Occupancy',
        cell: () => (
          <span className="text-muted-foreground text-sm">--</span>
        ),
      },

      // === RATES GROUP ===
      {
        accessorKey: 'default_monthly_rate',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Monthly Rate" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {formatCurrency(row.getValue('default_monthly_rate'))}
          </span>
        ),
      },
      {
        accessorKey: 'default_transient_rate',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Transient Rate" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm">
            {formatCurrency(row.getValue('default_transient_rate'))}
          </span>
        ),
      },

      // === ACTIONS ===
      {
        id: 'actions',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  onViewOnMap(row.original.id, row.original.area_id)
                }
              >
                <MapIcon className="mr-2 h-4 w-4" />
                View on Map
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onEditUnit(row.original.id)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit Unit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableHiding: false,
      },
    ],
    [onViewOnMap, onEditUnit]
  )

  // TanStack Table instance
  const table = useReactTable({
    data: flatUnits,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = (filterValue as string).toLowerCase()
      const unit = row.original
      return (
        unit.unit_code.toLowerCase().includes(search) ||
        (unit.name || '').toLowerCase().includes(search) ||
        unit.area_name.toLowerCase().includes(search) ||
        unit.area_code.toLowerCase().includes(search)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter: searchQuery,
    },
    initialState: {
      pagination: { pageSize: 50 },
    },
  })

  // Group visible rows by area_id for rendering
  const groupedRows = useMemo(() => {
    const rows = table.getRowModel().rows
    const groups = new Map<
      string,
      { area: SpaceManagementArea; rows: Row<SpaceManagementUnit>[] }
    >()

    for (const row of rows) {
      const areaId = row.original.area_id
      if (!groups.has(areaId)) {
        const area = areas.find((a) => a.id === areaId)
        if (area) {
          groups.set(areaId, { area, rows: [] })
        }
      }
      groups.get(areaId)?.rows.push(row)
    }

    return Array.from(groups.values())
  }, [table.getRowModel().rows, areas])

  // Check if an area is expanded (default: expanded)
  const isAreaExpanded = (areaId: string) =>
    expandedAreas[areaId] !== false

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => ({
      ...prev,
      [areaId]: prev[areaId] === false ? true : false,
    }))
  }

  const handleColumnGroupToggle = (key: ColumnGroupKey) => {
    setActiveColumnGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // CSV export
  const handleExport = () => {
    const headers = [
      'Building',
      'Building Code',
      'Unit Code',
      'Name',
      'Category',
      'Size (sqft)',
      'Status',
      '# Placed',
      '# Assigned',
      'Tenant',
      'Expected Rate',
      'Monthly Rate',
      'Transient Rate',
    ]
    const rows = table.getFilteredRowModel().rows.map((row) => {
      const u = row.original
      const expectedRate =
        u.lease_monthly_rate ?? u.unit_monthly_rate ?? u.default_monthly_rate
      return [
        u.area_name,
        u.area_code,
        u.unit_code,
        u.name || '',
        u.type ? UNIT_TYPE_LABELS[u.type] || u.type : '',
        u.usable_sqft?.toString() || '',
        UNIT_STATUS_LABELS[u.status] || u.status,
        u.placed_aircraft_count.toString(),
        u.assigned_aircraft_count.toString(),
        u.lease_client_name || '',
        expectedRate?.toString() || '',
        u.default_monthly_rate?.toString() || '',
        u.default_transient_rate?.toString() || '',
      ]
    })

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `space_management_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  // Count visible columns for colSpan
  const visibleColumnCount = table.getVisibleFlatColumns().length

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-2">
          <Input
            placeholder="Search units, buildings..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-8 w-[250px]"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              onClick={() => onSearchChange('')}
              className="h-8 px-2"
            >
              Reset
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={handleExport}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <ColumnGroupToggle
            activeGroups={activeColumnGroups}
            onToggle={handleColumnGroupToggle}
          />
        </div>
      </div>

      {/* Table with grouped rows */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading space data...
                  </div>
                </TableCell>
              </TableRow>
            ) : groupedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No spaces found. Create buildings and units to get started.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              groupedRows.map(({ area, rows: areaRows }) => {
                const expanded = isAreaExpanded(area.id)
                return (
                  <AreaGroup
                    key={area.id}
                    area={area}
                    rows={areaRows}
                    expanded={expanded}
                    onToggle={() => toggleArea(area.id)}
                    onEditArea={() => onEditArea(area.id)}
                    colSpan={visibleColumnCount}
                  />
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </div>
  )
}

// ============================================
// AREA GROUP (header + unit rows)
// ============================================

function AreaGroup({
  area,
  rows,
  expanded,
  onToggle,
  onEditArea,
  colSpan,
}: {
  area: SpaceManagementArea
  rows: Row<SpaceManagementUnit>[]
  expanded: boolean
  onToggle: () => void
  onEditArea: () => void
  colSpan: number
}) {
  return (
    <>
      {/* Area Header Row */}
      <TableRow
        className="cursor-pointer bg-muted/50 hover:bg-muted/80"
        onClick={onToggle}
      >
        <TableCell colSpan={colSpan} className="py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="font-semibold">{area.name}</span>
              <Badge variant="outline" className="font-mono text-xs">
                {area.area_code}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {AREA_TYPE_LABELS[area.type] || area.type}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>
                {formatNumber(area.total_usable_sqft)} sqft
              </span>
              <span>
                {area.total_units} unit{area.total_units !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <span className="text-green-600">{area.units_available} avail</span>
                <span>/</span>
                <span className="text-blue-600">{area.units_leased} leased</span>
                {area.units_maintenance > 0 && (
                  <>
                    <span>/</span>
                    <span className="text-orange-600">{area.units_maintenance} maint</span>
                  </>
                )}
              </span>
              {area.total_placed_aircraft > 0 && (
                <span className="flex items-center gap-1">
                  <Plane className="h-3 w-3" />
                  {area.total_placed_aircraft} placed
                </span>
              )}
              {area.total_assigned_aircraft > 0 && (
                <span className="flex items-center gap-1">
                  <Plane className="h-3 w-3" />
                  {area.total_assigned_aircraft} assigned
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation()
                  onEditArea()
                }}
                title="Edit building properties"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>

      {/* Unit Rows */}
      {expanded &&
        rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  )
}
